import { computed, Ref } from 'vue';
import { BcxTableFilter, BcxTableQuickFilter } from '@/models/BcxTable';

export type BcxTablePinnedItemPosition = {
  position: number;
  idField: string;
  idValue: string | number;
};

export type BcxTablePriorityItems = { // PriorityItems are always displayed first in a table.
  key: string;
  keyValues: unknown[];
};

export type BcxTableSearchFilterSortOptions = {
  searchCallback?: (searchTerm: string, obj: unknown) => boolean;
  filter?: BcxTableFilter;
  quickFilter?: BcxTableQuickFilter;
  sortValueCallback?: (fieldName: string, obj: unknown) => string | number;

  states?: string[];
  sortField?: string;
  isAscending?: boolean;
  searchTerm?: string;
  isQuickFilterActive?: boolean;

  pinnedItems?: BcxTablePinnedItemPosition[];
  priorityItems?: BcxTablePriorityItems;
};

const useBcxTableSearchFilterSort = (options: BcxTableSearchFilterSortOptions, rows: Ref<Array<unknown>>) => {
  const searchedRows = computed(() => {
    if (options.searchCallback && options.searchTerm) {
      return rows.value.filter((row) => options?.searchCallback?.(options?.searchTerm ?? '', row));
    }
    return rows.value;
  });

  const searchedFilteredRows = computed(() => {
    let filteredRow = searchedRows.value;
    if (options.quickFilter && options.quickFilter.stateCallback && options.isQuickFilterActive) {
      filteredRow = searchedRows.value.filter((row) => options?.quickFilter?.stateCallback(options?.states ?? [], row));
    }

    if (options.filter && options.states && options.filter.stateCallback) {
      filteredRow = filteredRow.filter((row) => options?.filter?.stateCallback(options?.states ?? [], row));
    }
    return filteredRow;
  });

  const searchedFilteredSortedRows = computed(() => {
    if (options.sortValueCallback && options.sortField) {
      const { isAscending } = options;
      return [
        ...searchedFilteredRows.value.sort((rowA, rowB) => {
          const aValue = options?.sortValueCallback?.(options?.sortField ?? '', rowA) ?? '';
          const bValue = options?.sortValueCallback?.(options?.sortField ?? '', rowB) ?? '';
          const comparison = typeof aValue === 'number' && typeof bValue === 'number' ? aValue - bValue : aValue.toString().localeCompare(bValue.toString());

          return isAscending ? comparison : -comparison;
        })
      ];
    }
    return searchedFilteredRows.value;
  });

  const searchedFilteredSortedRowsWithPinnedItems = computed(() => {
    if (options.pinnedItems?.length) {
      const newRows = [...searchedFilteredSortedRows.value] as Record<string, string | number>[];

      // First, position pinned positions by position.
      // So inserting them won't shift items that were inserted before.
      const sortedPinnedItems = [...options.pinnedItems].sort((a, b) => a.position - b.position);

      // remove all rows that will get pinned. This way, double-moving of rows
      // with shared positions will be prevented.
      const unPinnedRows = newRows.filter((row) => !sortedPinnedItems.find((item) => item.idValue === row[item.idField]));

      // insert all the pinned rows one after another:
      sortedPinnedItems.forEach((item) => {
        const row = newRows.find((row) => row[item.idField] === item.idValue);
        if (row) {
          unPinnedRows.splice(item.position, 0, row);
        }
      });

      return [...unPinnedRows];
    }
    return searchedFilteredSortedRows.value;
  });

  const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

  const searchedFilteredSortedRowsWithPinnedItemsAndPriorityItems = computed(() => {
    const base = searchedFilteredSortedRowsWithPinnedItems.value;
    const { priorityItems } = options;

    if (!priorityItems?.keyValues.length) return base;

    const priority: typeof base = [];
    const others: typeof base = [];

    base.forEach((item) => {
      const hit = isRecord(item) && priorityItems.keyValues.includes(item[priorityItems.key]);
      (hit ? priority : others).push(item);
    });

    return [...priority, ...others];
  });
  return {
    finalRows: searchedFilteredSortedRowsWithPinnedItemsAndPriorityItems
  };
};

export default useBcxTableSearchFilterSort;
